import { useSyncExternalStore } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, errorMessage } from "./api";
import { authStore } from "./auth-store";
import { openExternal } from "./external";

/** Signed-in user (decoded from the JWT) or null. Re-renders on login/logout/refresh. */
export function useAuth() {
  const state = useSyncExternalStore(authStore.subscribe, authStore.get, authStore.get);
  return {
    user: state.refreshToken ? state.user : null,
    isAuthenticated: state.refreshToken !== null,
  };
}

export function useLogout() {
  const queryClient = useQueryClient();
  return async () => {
    await api.auth.logout();
    queryClient.clear();
  };
}

/** Public client configuration (Turnstile site key, feature switches). */
export function useClientConfig() {
  return useQuery({ queryKey: ["config"], queryFn: api.config, staleTime: Infinity, retry: 1 });
}

/** Starts a Stripe Checkout session and leaves for Stripe (web build only). */
export function useCheckout() {
  return useMutation({
    mutationFn: (target: { kind: "subscription" } | { kind: "package"; id: string } | { kind: "analyzer-credits"; credits: number }) =>
      target.kind === "subscription"
        ? api.billing.subscriptionCheckout()
        : target.kind === "package"
          ? api.billing.packageCheckout(target.id)
          : api.billing.analyzerCreditsCheckout(target.credits),
    onSuccess: (r) => openExternal(r.url, { sameTab: true }),
    onError: (e) => toast.error("Checkout could not be started", { description: errorMessage(e) }),
  });
}

/**
 * Runs a POST once when a page opens (email confirmation…).
 * A query instead of `useMutation` + `useEffect`: React StrictMode mounts twice in development, and a mutation
 * started in an effect loses its observer on the remount, so the page would show the spinner forever.
 * The key makes the call happen only once per link; it is never refetched or retried.
 */
export function useRunOnce<T>(key: readonly unknown[], run: () => Promise<T>, enabled = true) {
  return useQuery({
    queryKey: ["run-once", ...key],
    queryFn: run,
    enabled,
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
